import {useEffect, useRef} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {tripService} from '@/services/tripService'
import {buildPolicyParams, hasPolicyParams} from '@/utils/policyParams'

export const POLICIES_KEY = ['policies', 'calculate']

/**
 * Cancellation / modification / waiting policies for the current trip, from
 * /trips/calculate-policies.
 *
 * TWO callers, ONE query — same endpoint, same cache key, same lifecycle; they differ only
 * in where the request params come from:
 *
 *  - usePolicies()        — the booking flow. Params from the in-progress booking in
 *                           sessionStorage (buildPolicyParams). Part of the modalPages gate
 *                           in HopnModal; the fetch belongs to opening the modal, never to
 *                           calculating the route.
 *  - usePolicies(params)  — the /confirm page, which is treated exactly like a new booking.
 *                           It has a TRIP, not a sessionStorage booking, and a Draft response
 *                           carries no edit_policy / cancellation_policy / tiers to read — so
 *                           it hands in {trip_direction, distance_meters} taken from the trip
 *                           and its calculate-route measurement.
 *
 * Static key + Infinity stale/gc: fetched once per page load and cached for the session, so
 * the gate's whole-modal loader covers the wait for every step from passengerQuantity on. It
 * never auto-refetches when the date/route/car change — only a full browser reload,
 * destinationBanner dropping the cache on a location edit, or the params-changed reset below.
 * Read anywhere via queryClient.getQueryData(POLICIES_KEY) / POLICY_CONSTANTS.
 */
export function usePolicies(overrideParams) {
    const queryClient = useQueryClient()
    const params = overrideParams
        ? {trip_direction: overrideParams.tripDirection, distance_meters: overrideParams.distanceMeters}
        : buildPolicyParams()
    // The booking flow must not fetch before the banner has a route — Infinity stale/gc would
    // cache the resulting "others / 0 bands" answer for the whole session (hasPolicyParams).
    // A trip page has no such ambiguity: the direction comes off the trip, so it fetches as
    // soon as the caller says it's ready and simply re-bands if the distance lands later.
    const enabled = overrideParams
        ? overrideParams.enabled !== false && !!params.trip_direction
        : hasPolicyParams(params)

    // One shared key means a cached answer outlives the trip it was calculated for (e.g. book
    // a ride, then open a DIFFERENT trip's /confirm in the same session). The bands are
    // distance-banded, so drop the cache when the params that produced it no longer match.
    const fetchedForRef = useRef(null)
    const paramsId = `${params.trip_direction}|${params.distance_meters}`
    useEffect(() => {
        if (!enabled) return
        if (fetchedForRef.current === null) { fetchedForRef.current = paramsId; return }
        if (fetchedForRef.current === paramsId) return
        fetchedForRef.current = paramsId
        queryClient.removeQueries({queryKey: POLICIES_KEY})
    }, [enabled, paramsId, queryClient])

    return useQuery({
        queryKey: POLICIES_KEY,
        queryFn: async () => {
            const result = await tripService.calculatePolicies(params)
            if (!result.success) throw new Error(result.error)
            return result.data
        },
        enabled,
        staleTime: Infinity,
        gcTime: Infinity,
    })
}
